import {
  getContext,
  getCurExternalChat,
  invoke,
  register,
} from '@wecom/jssdk'

/**
 * 注册企业微信 JS-SDK（应用身份）
 * 文档：https://developer.work.weixin.qq.com/document/path/90514
 * getCurExternalChat 必须使用应用身份进行注册
 */
export async function registerWeComSdk({ corpId, agentId, getSignature }) {
  if (!corpId || !agentId) {
    throw new Error('缺少 corpId 或 agentId')
  }

  // 同一 url 的签名结果短时复用，避免 config / agentConfig 各打一次后端
  const cache = new Map()
  async function loadSignatures(url) {
    if (!cache.has(url)) {
      cache.set(url, getSignature(url))
    }
    return cache.get(url)
  }

  return new Promise((resolve, reject) => {
    let settled = false

    const finish = (err) => {
      if (settled) return
      settled = true
      if (err) reject(err)
      else resolve()
    }

    register({
      corpId,
      agentId: Number(agentId),
      jsApiList: ['getCurExternalChat', 'getContext', 'getCurExternalContact'],
      async getConfigSignature(url) {
        const data = await loadSignatures(url)
        return data.configSignature
      },
      async getAgentConfigSignature(url) {
        const data = await loadSignatures(url)
        return data.agentConfigSignature
      },
      onAgentConfigSuccess() {
        finish()
      },
      onAgentConfigFail(err) {
        finish(new Error(err?.errMsg || err?.message || 'agentConfig 失败'))
      },
    })

    // 浏览器非企业微信环境不会触发 agentConfig；超时后放行，便于本地预览列表能力
    setTimeout(() => {
      if (!settled) finish()
    }, 4000)
  })
}

/** 获取进入页面的入口（group_chat_tools 表示客户群工具栏） */
export async function getEntryContext() {
  try {
    const res = await getContext()
    return { entry: res?.entry || '' }
  } catch {
    // ignore
  }

  return new Promise((resolve) => {
    invoke('getContext', {}, (res) => {
      if (res.err_msg === 'getContext:ok') {
        resolve({ entry: res.entry || '' })
      } else {
        resolve({ entry: '' })
      }
    }).catch(() => resolve({ entry: '' }))
  })
}

/**
 * 获取当前客户群的群 ID
 * 文档：https://developer.work.weixin.qq.com/document/path/92095
 * 仅从客户群/班级群聊天工具栏进入时可成功
 */
export async function getCurExternalChatId() {
  try {
    const res = await getCurExternalChat()
    if (res?.chatId) return res.chatId
    throw new Error(res?.errMsg || 'getCurExternalChat 未返回 chatId')
  } catch (err) {
    // 兼容旧版 invoke 回调形态
    return new Promise((resolve, reject) => {
      invoke('getCurExternalChat', {}, (res) => {
        if (res.err_msg === 'getCurExternalChat:ok') {
          resolve(res.chatId)
        } else {
          reject(new Error(res.err_msg || err?.message || '获取当前群 ID 失败'))
        }
      }).catch(() => reject(err))
    })
  }
}
